import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Determines where to redirect a user after login based on their account status.
 *
 * Status flow:
 *   1. Email not verified        → /verify-email  (handled before login succeeds)
 *   2. Basic info not submitted  → /basic-info
 *   3. Account pending/rejected  → /account-pending
 *   4. Fully approved            → returnUrl (dashboard / home)
 */
const getPostLoginRedirect = (user, returnUrl = '/') => {
  if (!user) return returnUrl;

  // Admins bypass all profile-completion checks (same as VerificationGuard)
  if (user.isAdmin === true) {
    return '/search';
  }

  const accountStatus = user.accountStatus;

  // Step 2: Basic info not yet submitted
  if (!accountStatus || accountStatus === 'not_submitted') {
    return '/basic-info';
  }

  // Step 3: Pending or rejected
  if (accountStatus === 'pending' || accountStatus === 'rejected') {
    return '/account-pending';
  }

  // Step 4: Approved but mandatory profile fields missing (must match VerificationGuard)
  const requiredFields = ['firstName', 'lastName', 'image', 'country', 'about'];
  const missingFields = requiredFields.filter(f => !user[f] || user[f] === '');
  if (missingFields.length > 0) {
    return '/edit-profile?mandatory=true';
  }

  // Step 5: Fully approved & profile complete → dashboard
  return '/search';
};

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingOAuth, setProcessingOAuth] = useState(false);
  const [usePasswordless, setUsePasswordless] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [loginCode, setLoginCode] = useState(['', '', '', '', '', '']);
  const [sendingCode, setSendingCode] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { login, loadUser, isAuthenticated, user } = useAuth();

  // Get the return URL from state or query params, default to homepage
  const returnUrl = location.state?.returnUrl || new URLSearchParams(location.search).get('returnUrl') || '/';

  // Check for OAuth session_id in URL fragment on mount
  useEffect(() => {
    const processOAuthSession = async () => {
      // Check if session_id exists in URL fragment
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]*)/);
      
      if (sessionIdMatch && sessionIdMatch[1]) {
        const sessionId = sessionIdMatch[1];
        setProcessingOAuth(true);
        
        try {
          console.log('🔍 Processing OAuth session_id:', sessionId);
          
          // Call backend to process session_id
          const response = await axios.post(
            `${API_URL}/api/auth/oauth/session`,
            {},
            {
              headers: {
                'X-Session-ID': sessionId,
                'Content-Type': 'application/json'
              },
              withCredentials: true // Important for cookies
            }
          );
          
          console.log('✅ OAuth response received:', response.data);
          
          if (response.data && (response.data._id || response.data.id || response.data.email)) {
            // OAuth session processed successfully
            // The response is the user object directly
            const userData = response.data;
            
            // Store session token in localStorage
            // The backend also sets it as httpOnly cookie
            localStorage.setItem('token', sessionId);
            
            // Set user in AuthContext with complete user object
            login(userData, sessionId);
            
            toast({
              title: "Welcome!",
              description: `Successfully logged in with Google as ${userData.firstName}`,
            });
            
            console.log('✅ User logged in via OAuth:', userData);
            
            // Clean URL fragment
            window.history.replaceState(null, '', window.location.pathname);

            // Redirect based on account status
            setTimeout(() => {
              navigate(getPostLoginRedirect(userData, returnUrl), { replace: true });
            }, 500);
          } else {
            throw new Error('Invalid response from OAuth session endpoint');
          }
        } catch (error) {
          console.error('❌ OAuth session processing error:', error);
          console.error('Error details:', error.response?.data);
          
          const errorMessage = error.response?.data?.detail || error.message || "Failed to process Google login";
          
          toast({
            title: "Google Login Failed",
            description: errorMessage,
            variant: "destructive",
          });
          
          // Clean URL fragment
          window.history.replaceState(null, '', window.location.pathname);
        } finally {
          setProcessingOAuth(false);
        }
      }
    };
    
    processOAuthSession();
  }, []);

  // If already authenticated, redirect based on account status
  useEffect(() => {
    if (isAuthenticated && !processingOAuth) {
      navigate(getPostLoginRedirect(user, returnUrl), { replace: true });
    }
  }, [isAuthenticated, navigate, returnUrl, processingOAuth, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast({
        title: "Missing Fields",
        description: "Please enter email and password",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const data = await authAPI.login(formData);
      if (data.success) {
        login(data.user, data.token);
        toast({
          title: "Login Successful",
          description: "Welcome back to qbool!",
        });

        // Determine redirect based on user account status
        const user = data.user;
        const redirectPath = getPostLoginRedirect(user, returnUrl);

        setTimeout(() => {
          navigate(redirectPath, { replace: true });
        }, 800);
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || "Invalid email or password";

      // Check if error is due to unverified email
      if (errorMessage.includes("verify your email") || errorMessage.includes("not verified") || errorMessage.includes("verification")) {
        navigate('/verify-email', { state: { email: formData.email, fromLogin: true } });
      } else {
        toast({
          title: "Login Failed",
          description: errorMessage,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle sending login code
  const handleSendLoginCode = async () => {
    if (!formData.email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setSendingCode(true);
    try {
      await authAPI.sendLoginCode(formData.email);
      setCodeSent(true);
      toast({
        title: "Code Sent",
        description: "Check your email for the login code",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSendingCode(false);
    }
  };

  // Handle code input change
  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...loginCode];
    newCode[index] = value.slice(-1);
    setLoginCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      document.getElementById(`login-code-${index + 1}`)?.focus();
    }
  };

  // Handle passwordless login
  const handleLoginWithCode = async (e) => {
    e.preventDefault();
    const code = loginCode.join('');
    
    if (code.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter the complete 6-digit code",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const data = await authAPI.loginWithCode(formData.email, code);
      if (data.success) {
        login(data.user, data.token);
        toast({
          title: "Login Successful",
          description: "Welcome back to qbool!",
        });
        setTimeout(() => {
          navigate(getPostLoginRedirect(data.user, returnUrl), { replace: true });
        }, 800);
      }
    } catch (error) {
      toast({
        title: "Login Failed",
        description: error.response?.data?.detail || "Invalid code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    toast({
      title: "Coming Soon!",
      description: "Google login will be available in the future.",
    });
  };

  const handleFacebookLogin = () => {
    toast({
      title: "Coming Soon!",
      description: "Facebook login will be available in the future.",
    });
  };

  if (processingOAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardContent className="pt-12 pb-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Processing your login...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">
            <div className="text-3xl font-bold mb-2">
              <span className="text-navy-500">qbool</span>
            </div>
            <p className="text-gray-600 text-base font-normal">Login to your account</p>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Login Method Toggle */}
          <div className="flex gap-2 mb-6 p-1 bg-gray-100 rounded-lg">
            <button
              type="button"
              onClick={() => {
                setUsePasswordless(false);
                setCodeSent(false);
                setLoginCode(['', '', '', '', '', '']);
              }}
              className={`flex-1 py-2 px-4 rounded-md transition-all ${
                !usePasswordless 
                  ? 'bg-white text-navy-500 shadow-sm font-medium' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Lock className="w-4 h-4 inline mr-2" />
              Password
            </button>
            <button
              type="button"
              onClick={() => {
                setUsePasswordless(true);
                setCodeSent(false);
                setLoginCode(['', '', '', '', '', '']);
              }}
              className={`flex-1 py-2 px-4 rounded-md transition-all ${
                usePasswordless 
                  ? 'bg-white text-navy-500 shadow-sm font-medium' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Mail className="w-4 h-4 inline mr-2" />
              Email Code
            </button>
          </div>

          <form onSubmit={usePasswordless ? handleLoginWithCode : handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10"
                  disabled={codeSent && usePasswordless}
                  required
                />
              </div>
            </div>

            {/* Password Login */}
            {!usePasswordless && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" />
                    <span className="text-sm text-gray-600">Remember me</span>
                  </label>
                  <Link to="/forgot-password" className="text-sm text-navy-500 hover:text-navy-600">
                    Forgot password?
                  </Link>
                </div>

                <Button type="submit" className="w-full bg-mint-500 hover:bg-mint-600 text-white" disabled={loading}>
                  {loading ? "Logging in..." : "Login"}
                </Button>
              </>
            )}

            {/* Passwordless Login */}
            {usePasswordless && !codeSent && (
              <Button 
                type="button" 
                onClick={handleSendLoginCode}
                className="w-full bg-mint-500 hover:bg-mint-600 text-white" 
                disabled={sendingCode}
              >
                {sendingCode ? "Sending Code..." : "Send Login Code"}
              </Button>
            )}

            {/* Code Input */}
            {usePasswordless && codeSent && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                    Enter the 6-digit code sent to your email
                  </label>
                  <div className="flex gap-2 justify-center mb-4">
                    {loginCode.map((digit, index) => (
                      <input
                        key={index}
                        id={`login-code-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-mint-500 focus:ring-2 focus:ring-red-200 outline-none transition"
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 text-center mb-4">
                    Code expires in 10 minutes
                  </p>
                </div>

                <Button type="submit" className="w-full bg-mint-500 hover:bg-mint-600 text-white" disabled={loading}>
                  {loading ? "Verifying..." : "Login"}
                </Button>

                <button
                  type="button"
                  onClick={handleSendLoginCode}
                  disabled={sendingCode}
                  className="w-full text-sm text-navy-500 hover:text-navy-600 underline"
                >
                  {sendingCode ? "Sending..." : "Resend Code"}
                </button>
              </>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-navy-500 hover:text-navy-600 font-semibold">
                Register Free
              </Link>
            </p>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <Button variant="outline" className="w-full" onClick={handleGoogleLogin} type="button">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google
              </Button>
              <Button variant="outline" className="w-full" onClick={handleFacebookLogin} type="button">
                <svg className="w-5 h-5 mr-2" fill="#1877F2" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;